import { useState } from 'react';
import { useBrokerDetailsStore } from '../../../../state/brokerDetailsStore';
import { useBrokerSettingsUpdateMutation } from '../../../../queries/brokerDetailsQueries';
import {
    handleAdminSuccess,
    handleAdminError,
    handleAdminWarning,
} from '../../../../../../common/SharedFunctions';
import { BrokerApi } from '@apply/api';
import { useWaitOverlayStore } from '@apply/components';

export interface UseBrokerSettingsSubmitReturn {
    handleBrokerSettingsSubmit: () => Promise<void>;
    isSubmitting: boolean;
}

const buildSaveRequest = (s: ReturnType<typeof useBrokerDetailsStore.getState>): BrokerApi.IBrokerSettings => {
    return {
        firstName: s.firstName,
        lastName: s.lastName,
        arizonaLicenseNumber: s.arizonaLicenseNumber,
        wireBankName: s.wireBankName,
        wireRecipientName: s.wireRecipientName,
        wireAccountLastFourDigits: s.wireAccountLastFourDigits,
        businessName: s.businessName,
        businessState: s.businessState,
        title: s.title,
        businessAddress: s.businessAddress,
        homeAddress: s.sameAsContactAddress ? s.businessAddress : s.homeAddress,
        phoneNumberA: s.phoneNumberA,
        phoneNumberAType: s.phoneNumberAType,
        phoneNumberB: s.phoneNumberB,
        phoneNumberBType: s.phoneNumberBType,
        emailAddressA: s.email,
        secondaryEmail: s.secondaryEmail,
        companyLogoUrl: s.companyLogoUrl,
        dateOfBirth: s.dateOfBirth,
    };
};

const _buildRequest = async () => {
    return buildSaveRequest(useBrokerDetailsStore.getState());
};

export const useBrokerSettingsSubmit = (validate: () => { isValid: boolean }): UseBrokerSettingsSubmitReturn => {
    const brokerSettingsUpdateMutation = useBrokerSettingsUpdateMutation();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleBrokerSettingsSubmit = async () => {
        if (!validate().isValid) {
            handleAdminWarning('Please fix the invalid fields.');
            return;
        }
        const { setWaiting } = useWaitOverlayStore.getState();
        try {
            const brokerId = useBrokerDetailsStore.getState().brokerId;
            const data = await _buildRequest();
            setIsSubmitting(true);
            setWaiting(true, 'Saving Your Settings...');
            await brokerSettingsUpdateMutation.mutateAsync({
                brokerId,
                data,
            });
            handleAdminSuccess('Settings saved!');
        } catch (ex) {
            handleAdminError(
                ex instanceof Error ? ex : new Error(String(ex)),
                'An unexpected error has occurred. Please try again.'
            );
        } finally {
            setIsSubmitting(false);
            setWaiting(false);
        }
    };

    return {
        handleBrokerSettingsSubmit,
        isSubmitting,
    };
};
